interface ShimmerEffectProps {
  shimmerColor?: string;
  /** Angular size of the bright wedge in the conic gradient. */
  spread?: string;
  /** Border radius applied to the shimmer-slide container. Match the parent button's radius. */
  rounded?: string;
}

/**
 * Rotating conic-gradient sheen for primary buttons — matches shyft.ai's pattern.
 *
 * Renders two nested elements inside a button:
 *   1. shimmer-slide container — a square (h-[100cqh] [aspect-ratio:1]) sliding
 *      horizontally across the button. Sets the container query for the spinner.
 *   2. spin-around spinner — a conic gradient at -inset-full that rotates inside
 *      the slide container, producing a bright wedge that orbits the button.
 *
 * The parent button MUST have: `group relative overflow-hidden` plus a solid
 * background. A thin border (e.g. `border border-white/10`) reads best against
 * the rotating sheen.
 *
 * Place the button content in a sibling span with `relative z-10` so it paints
 * above the rotating gradient.
 */
export default function ShimmerEffect({
  shimmerColor = "#ffffff",
  spread = "90deg",
  rounded = "9999px",
}: ShimmerEffectProps) {
  const styleVars = {
    "--shimmer-color": shimmerColor,
    "--spread": spread,
    "--radius": rounded,
  } as React.CSSProperties;

  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden [container-type:size] [border-radius:var(--radius)]"
      style={styleVars}
    >
      <span className="absolute inset-0 h-[100cqh] motion-safe:animate-shimmer-slide [aspect-ratio:1] [border-radius:0]">
        <span className="absolute w-auto motion-safe:animate-spin-around rotate-0 [inset:-125%] [background:conic-gradient(from_calc(270deg-(var(--spread)*0.5)),transparent_0,var(--shimmer-color)_var(--spread),transparent_var(--spread))]" />
      </span>
    </span>
  );
}
